'use client';

import { useStickyVisibility } from '@shared/hooks';
import { Spinner } from '@shared/icons';
import { twMerge } from '@shared/themes';
import { NumberFormats, useCurrencyFormatter } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { useGlobalSheet } from '../../../../global-sheets/context/GlobalSheetContext';
import { StatusNotification } from '../../../../StatusNotification/StatusNotification';
import { StepperInput } from '../../../../StepperInput/StepperInput';
import { Summary, SummaryActionPrimary, SummaryContent, SummaryItem, SummaryTotal } from '../../../../Summary/Summary';
import { buildScratchcardEncodedTicket } from '../../../utils/buildScratchcardEncodedTicket';

const QUICK_INCREMENTS = [1, 5, 10, 25];

type ScratchcardPurchasePanelProps = {
  unitPrice: number;
  title: string;
  localisedName: string;
};

type ScratchcardStickyPurchaseBarProps = {
  countText: string;
  totalPrice: string;
  checkoutLabel: string;
  loadingLabel: string;
  isCheckoutLoading: boolean;
  onCheckout: () => void;
  className?: string;
};

/**
 * ScratchcardStickyPurchaseBar component
 * @param countText - The text to display the count
 * @param totalPrice - The text to display the total price
 * @param checkoutLabel - The text to display the checkout button
 * @param loadingLabel - Accessible label for the checkout spinner
 * @param isCheckoutLoading - Whether checkout is in progress
 * @param onCheckout - The function to call when the user clicks the checkout button
 * @param className - The class name to apply to the component
 */
function ScratchcardStickyPurchaseBar({ countText, totalPrice, checkoutLabel, loadingLabel, isCheckoutLoading, onCheckout, className }: ScratchcardStickyPurchaseBarProps) {
  return (
    <div
      className={twMerge('isolate fixed z-10 left-0 right-0 ll-grid-container bg-background rounded-t-lg shadow-top-m', className)}
      data-testid="scratchcard-sticky-purchase-bar"
    >
      <div className="ll-container-full md:ll-container-inner flex items-center justify-between gap-4 p-4">
        <div className="flex flex-col">
          <span className="paragraph-s" data-testid="scratchcard-sticky-purchase-bar-count">
            {countText}
          </span>
          <span className="paragraph-strong" data-testid="scratchcard-sticky-purchase-bar-total">
            {totalPrice}
          </span>
        </div>
        <SummaryActionPrimary className="mt-0 w-auto px-6" onClick={onCheckout} disabled={isCheckoutLoading} data-testid="scratchcard-sticky-purchase-bar-checkout">
          <span className="relative inline-flex items-center justify-center">
            <span className={isCheckoutLoading ? 'invisible' : undefined} aria-hidden={isCheckoutLoading}>
              {checkoutLabel}
            </span>
            {isCheckoutLoading ? <Spinner size="small" className="absolute animate-spin w-5" aria-label={loadingLabel} data-testid="scratchcard-checkout-spinner" /> : null}
          </span>
        </SummaryActionPrimary>
      </div>
    </div>
  );
}

/**
 * ScratchcardPurchasePanel component
 * @param unitPrice - The unit price of the scratchcard
 * @param title - The scratchcard title used to build the encoded cart ticket
 * @param localisedName - The display name shown after purchase
 * @returns The ScratchcardPurchasePanel component
 */
function ScratchcardPurchasePanel({ unitPrice, title, localisedName }: ScratchcardPurchasePanelProps) {
  const t = useTranslations('scratchcards.product_page');
  const tForm = useTranslations('form');
  const { openCheckoutSheet } = useGlobalSheet();
  const currencyFormatter = useCurrencyFormatter();
  const [quantity, setQuantity] = useState(1);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [hasCheckoutError, setHasCheckoutError] = useState(false);
  const summaryRef = useRef<HTMLDivElement>(null);
  const { isVisible: shouldShowSticky } = useStickyVisibility({ targetRef: summaryRef });

  const formattedTotal = currencyFormatter(unitPrice * quantity)
    .format(NumberFormats.WITH_DECIMALS)
    .get();
  const checkoutLabel = t('go_to_checkout');
  const loadingLabel = tForm('aria.loading');
  const checkoutErrorTitle = t('checkout_error_title');
  const checkoutErrorDescription = t('checkout_error_description');
  const countText = t('scratchcard_count', { count: quantity });
  const stickyClasses = twMerge('transition-all duration-300', shouldShowSticky ? 'bottom-0' : '-bottom-20 pointer-events-none');
  const checkoutContent = isCheckoutLoading ? (
    <Spinner size="small" className="animate-spin w-5" aria-label={loadingLabel} data-testid="scratchcard-checkout-spinner" />
  ) : (
    checkoutLabel
  );

  const onCheckout = async () => {
    setHasCheckoutError(false);
    setIsCheckoutLoading(true);
    try {
      const result = await openCheckoutSheet({
        encoded: buildScratchcardEncodedTicket(title, quantity),
        ticketDetails: [{ text: countText, price: formattedTotal, type: 'NORMAL' }],
        productDisplayName: localisedName,
      });

      // Keep the spinner up while the browser leaves for login.
      if (result === 'signing_in') return;
    } catch {
      setHasCheckoutError(true);
    }
    setIsCheckoutLoading(false);
  };

  return (
    <div className="flex flex-col gap-6" data-testid="scratchcard-purchase-panel">
      <StepperInput
        value={quantity}
        onChange={setQuantity}
        min={1}
        label={t('quantity')}
        quickIncrements={QUICK_INCREMENTS}
        decrementLabel={t('decrement')}
        incrementLabel={t('increment')}
        quickIncrementLabel={amount => t('quick_increment', { amount })}
      />
      <div ref={summaryRef}>
        <Summary data-testid="scratchcard-checkout-summary">
          <SummaryContent>
            <SummaryTotal label={t('total')} value={formattedTotal} />
            <SummaryItem label={countText} value={formattedTotal} data-testid="scratchcard-checkout-count" />
            <SummaryActionPrimary onClick={onCheckout} disabled={isCheckoutLoading} data-testid="scratchcard-go-to-checkout">
              {checkoutContent}
            </SummaryActionPrimary>
            {hasCheckoutError ? <StatusNotification type="error" label={checkoutErrorTitle} description={checkoutErrorDescription} className="mt-4" /> : null}
          </SummaryContent>
        </Summary>
      </div>
      <ScratchcardStickyPurchaseBar
        countText={countText}
        totalPrice={formattedTotal}
        checkoutLabel={checkoutLabel}
        loadingLabel={loadingLabel}
        isCheckoutLoading={isCheckoutLoading}
        onCheckout={onCheckout}
        className={stickyClasses}
      />
    </div>
  );
}

export { ScratchcardPurchasePanel, type ScratchcardPurchasePanelProps };
